import { DecimalPipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { LargeCardService } from 'app/modules/my-limegear/components/standalone-components/services/large-card.service';
import { LimeGearCharts } from 'app/modules/shared/classes/chart-data';
import { ICamparisonInfo, ReusableFunctions } from 'app/modules/shared/classes/reusable-functions';
import { SmallCardData } from 'app/modules/shared/interfaces/card-data';
import { DisplayOptions } from 'app/modules/shared/interfaces/display-options';
import { NoDataActionType, NoDataSourceType } from 'app/modules/shared/interfaces/no-data';
import { UserAccessService } from 'app/modules/shared/services/user-access.service';
import { GLAAGSmallCardData } from '../../interfaces/incentives-data';
import { IncentivesService } from '../../services/incentives.service';
import { ICardInfo } from 'app/modules/my-limegear/components/standalone-components/interfaces/stanalone-components.data';

@Component({
    selector: 'app-financials-at-a-glance',
    templateUrl: './financials-at-a-glance.component.html',
    styleUrls: ['./financials-at-a-glance.component.scss'],
})
export class FinancialsAtAGlanceComponent implements OnInit {
    pageTitle: string = 'Financials At a Glance';
    displayOptions: DisplayOptions[];
    financialsSmallCards: ICardInfo[];
    smallCard: any;
    smallCards: [];
    financialsLargeCards: ICardInfo[] = [{}, {}];
    action: NoDataActionType = NoDataActionType.pageStart;
    limeGearCharts: LimeGearCharts.LimeGearChart;
    fromDatePeriod: string;
    compareTo: string;
    hFilterJSON: string;
    cardDrilldownUrl: string;

    constructor(
        private incentivesService: IncentivesService,
        private _router: Router,
        private reusableFunctions: ReusableFunctions,
        private largecardService: LargeCardService,
        private decimalPipe: DecimalPipe,
        private userAccessService: UserAccessService
    ) {}

    ngOnInit(): void {
        this.displayOptions = this.reusableFunctions.getCurrentDisplayOptions(this._router.url);
        this.hFilterJSON = this.reusableFunctions.getHierarchyFilters();
        this.limeGearCharts = new LimeGearCharts.LimeGearChart(
            this._router,
            this.reusableFunctions,
            this.decimalPipe,
            this.userAccessService,
            this.incentivesService
        );
        this.setDisplayOptionsSelected();
        this.initializeSmallCards();
        this.initializeLargeCards();
        this.loadSmallCardData();
        this.loadLargeCardData();
    }

    initializeSmallCards(): void {
        this.financialsSmallCards = [
            {
                index: 0,
                title: 'TOTAL REVENUE',
                description: 'Revenue',
            },
            {
                index: 1,
                title: 'TOTAL EXPENSES',
                description: 'Expense',
            },
            {
                index: 2,
                title: 'TOTAL NET INCOME',
                description: 'Net Income',
            },
            {
                index: 3,
                title: 'LOAN REVENUE',
                description: 'Revenue',
            },
            {
                index: 4,
                title: 'LOAN EXPENSES',
                description: 'Expense',
            },
            {
                index: 5,
                title: 'LOAN NET INCOME',
                description: 'Net Income',
            },
            {
                index: 6,
                title: 'VOLUME FUNDED',
                description: 'Funded',
            },
        ];

        this.financialsSmallCards.forEach((card) => {
            card.card = Object.assign({}, this.getEmptySmallCardData(card));
        });
    }

    initializeLargeCards(): void {
        this.financialsLargeCards = [
            {
                name: 'Total Revenue',
                colorObject: {
                    'Loan Revenue': '#57D785',
                    Revenue: '#694DD6',
                },
                urlSegment: 'at-a-glance',
                description: 'Revenue',
                type: 'amount',
                alternateType: 'bps',
                yLabel: 'Revenue ($)',
                isBPS: false,
            },
            {
                name: 'Total Revenue (BPS)',
                colorObject: {
                    'Loan Revenue': '#57D785',
                    Revenue: '#694DD6',
                },
                urlSegment: 'at-a-glance2',
                description: 'Revenue',
                type: 'bps',
                alternateType: 'amount',
                yLabel: 'Revenue (BPS)',
                isBPS: true,
            },
            {
                name: 'Total Expenses',
                colorObject: {
                    'Loan Expense': '#57D785',
                    Expense: '#694DD6',
                },
                urlSegment: 'at-a-glance3',
                description: 'Expense',
                type: 'amount',
                alternateType: 'bps',
                yLabel: 'Expenses ($)',
                isBPS: false,
            },
            {
                name: 'Total Expenses (BPS)',
                colorObject: {
                    'Loan Expense': '#57D785',
                    Expense: '#694DD6',
                },
                urlSegment: 'at-a-glance4',
                description: 'Expense',
                type: 'bps',
                alternateType: 'amount',
                yLabel: 'Expenses (BPS)',
                isBPS: true,
            },
            {
                name: 'Total Net Income',
                colorObject: {
                    'Loan Net Income': '#57D785',
                    'Net Income': '#694DD6',
                },
                urlSegment: 'at-a-glance5',
                description: 'Net Income',
                type: 'amount',
                alternateType: 'bps',
                yLabel: 'Net Income ($)',
                isBPS: false,
            },
            {
                name: 'Total Net Income (BPS)',
                colorObject: {
                    'Loan Net Income': '#57D785',
                    'Net Income': '#694DD6',
                },
                urlSegment: 'at-a-glance6',
                description: 'Net Income',
                type: 'bps',
                alternateType: 'amount',
                yLabel: 'Net Income (BPS)',
                isBPS: true,
            },
        ];

        this.financialsLargeCards.forEach((largeCard) => {
            this.largecardService.initializeLargeCard(
                largeCard,
                this.action,
                '/reporting/financials'
            );
        });
    }

    loadSmallCardData() {
        const parameters = {
            fromDatePeriod: this.fromDatePeriod,
            priorCompareTo: this.compareTo,
            hFilterJSON: this.hFilterJSON,
        };

        this.incentivesService.getGLAAGSmallCardData(parameters, 0).subscribe(
            (data) => {
                this.prepareSmallCards(data, true);
            },
            (error) => {
                console.log('error: ', error);
            }
        );
        this.incentivesService.getGLAAGSmallCardData(parameters, 1).subscribe(
            (data) => {
                this.prepareSmallCards(data, false);
            },
            (error) => {
                console.log('error: ', error);
            }
        );
    }

    loadLargeCardData() {
        const parameters = {
            fromDatePeriod: this.fromDatePeriod,
            priorCompareTo: this.compareTo,
            hFilterJSON: this.hFilterJSON,
        };

        this.incentivesService.getGLAAGLargeCardData(parameters).subscribe(
            (data) => {
                for (let largeCard of this.financialsLargeCards) {
                    this.prepareLargeCards(data, largeCard, null);
                }
            },
            (error) => {
                console.log('error: ', error);
            }
        );
    }

    prepareSmallCards(data: GLAAGSmallCardData[], withoutLoan: boolean) {
        let noData;

        if (!data) {
            noData = true;
        } else {
            noData =
                data.length > 0
                    ? data[0].nbr === 0 && data[0].bps === 0 && data[0].amount === 0
                    : true;
        }

        for (let i = 0; i < this.financialsSmallCards.length; i++) {
            let financialsSmallCards = this.financialsSmallCards[i];

            if ((withoutLoan && i >= 3) || (!withoutLoan && i < 3)) {
                continue;
            }

            if (noData) {
                financialsSmallCards.card = Object.assign(
                    {},
                    this.getEmptySmallCardData(financialsSmallCards, false, true, this.action)
                );
            } else {
                financialsSmallCards.card = this.getEmptySmallCardData(financialsSmallCards);
                const correspondingData = data.filter(
                    (dataInfo) => financialsSmallCards.description === dataInfo.description
                );
                const correspondingLoanData = data.filter(
                    (dataInfo) => financialsSmallCards.description === dataInfo.description
                );
                const currentData = withoutLoan ? correspondingData[0] : correspondingLoanData[0];
                const compareData = withoutLoan ? correspondingData[1] : correspondingLoanData[1];
                const lowerIsGood = financialsSmallCards.description === 'Expense';
                const valueType = compareData.description === 'Funded' ? 'volume' : 'amount';
                let compareValue = this.getCompareValue(
                    currentData[valueType],
                    compareData[valueType]
                );
                const compareNum = compareValue;
                compareValue = this.reusableFunctions.getCaret(
                    compareValue,
                    lowerIsGood,
                    null,
                    true
                );

                if (correspondingData.length) {
                    financialsSmallCards.card.routerLink = '';
                    financialsSmallCards.card.urlSegment = '';
                    financialsSmallCards.card.hasData = true;
                    financialsSmallCards.card.isDataReady = true;
                    financialsSmallCards.card.mainValue =
                        this.reusableFunctions.getLargeCurrencyFormat(currentData[valueType]);
                    financialsSmallCards.card.fullValue = currentData[valueType];
                    financialsSmallCards.card.comparisonValue = compareValue.comparison;
                    financialsSmallCards.card.faIconColor =
                        (!lowerIsGood && compareNum > 0) || (lowerIsGood && compareNum < 0)
                            ? '#008800'
                            : '#E0000E';
                    financialsSmallCards.card.svgData = compareValue.svgData;
                    financialsSmallCards.card.bottomCardData = this.getBottomCardData(currentData);
                    financialsSmallCards.card.datePeriod = this.fromDatePeriod;
                    financialsSmallCards.card.compareTo = this.compareTo;
                    financialsSmallCards.card.hFilterJSON = this.hFilterJSON;
                }
            }

            this.financialsSmallCards[i] = Object.assign({}, financialsSmallCards);
        }
    }

    prepareLargeCards(dataSet, cardInfo: ICardInfo, action: NoDataActionType) {
        const noData = dataSet === null || !(dataSet.length > 0);
        if (noData) {
            cardInfo = Object.assign({}, this.getEmptyLargeCardData());
        } else {
            let data = dataSet.map((obj) => ({ ...obj }));
            let thisPeriodData = [];
            let comparePeriodData = [];
            let chartOptions: Highcharts.Options;
            let comparison: ICamparisonInfo;
            let sumBPSThisPeriod: number;
            let sumVolumeThisPeriod: number;
            let uniquePeriods = this.reusableFunctions.getUniqueItemList(data, 'period');
            uniquePeriods = uniquePeriods.sort((a, b) => a - b);

            if (data.length > 0) {
                thisPeriodData = data.filter(
                    (item) =>
                        item.periodGroup === 'This Period' &&
                        item.description.includes(cardInfo.description)
                );
                this.sanitizeLoanData(thisPeriodData);
                comparePeriodData = data.filter(
                    (item) =>
                        item.periodGroup === 'Compare Period' &&
                        item.description === cardInfo.description
                );
                const currentPeriodDates = thisPeriodData
                    .filter((item) => item.description.includes('Loan'))
                    .map((item) => item.yearMonthCalendar);
                const comparePeriodDates = comparePeriodData.map((item) => item.yearMonthCalendar);

                let chartData = this.incentivesService.setChartData(
                    thisPeriodData,
                    comparePeriodData,
                    cardInfo.type,
                    cardInfo.alternateType,
                    'description',
                    cardInfo.description,
                    cardInfo.colorObject,
                    'This Month'
                );

                sumVolumeThisPeriod = this.reusableFunctions.calculateSumByProperty(
                    thisPeriodData,
                    cardInfo.type
                );
                sumBPSThisPeriod = this.reusableFunctions.calculateSumByProperty(
                    thisPeriodData,
                    'bps'
                );
                const sumBPSLastPeriod = this.reusableFunctions.calculateSumByProperty(
                    comparePeriodData,
                    'bps'
                );
                comparison = this.reusableFunctions.calculateComparisonPercentage(
                    sumBPSThisPeriod,
                    sumBPSLastPeriod
                );

                let processedChartSeries = this.incentivesService.setAllChartSeriesOptions(
                    cardInfo,
                    chartData.lineData,
                    chartData.stackColumnsData,
                    'Previous Period',
                    '/reporting/financials',
                    'Prior Period',
                    'All',
                    'All',
                    this.limeGearCharts
                );

                chartOptions = this.limeGearCharts.createNewAAGStackedChart(
                    'Type',
                    cardInfo.yLabel,
                    processedChartSeries,
                    currentPeriodDates,
                    comparePeriodDates,
                    cardInfo.isBPS,
                    'reporting/financials/at-a-glance',
                    'at-a-glance',
                    'Prior Period',
                    'Previous Period',
                    'All',
                    'All',
                    this.hFilterJSON
                );
            }

            cardInfo.largeCardInteriorOptions = this.incentivesService.updateChartData(
                cardInfo,
                chartOptions,
                data.length > 0,
                true,
                action,
                comparison,
                sumBPSThisPeriod,
                sumVolumeThisPeriod,
                true,
                false,
                'All',
                'All',
                'Prior Period'
            );
        }
    }

    getCompareValue(currentAmount: number, compareAmount: number) {
        let percent;
        if (currentAmount === 0 && compareAmount === 0) {
            return 0;
        }
        percent = ((currentAmount - compareAmount) / currentAmount) * 100;
        return percent;
    }

    getEmptySmallCardData(
        cardInf: ICardInfo,
        hasData = false,
        isDataReady = false,
        action = null
    ): SmallCardData {
        return {
            title: cardInf.title,
            mainValue: '',
            unit: '',
            faIcon: '',
            faIconColor: '',
            comparisonValue: '',
            urlSegment: cardInf.drillDUrlSegment,
            routerLink: this.cardDrilldownUrl,
            svgData: '',
            isDataReady: isDataReady,
            hasData: hasData,
            searchTerm: '',
            action,
            noDataSource: NoDataSourceType.verySmallDataCard,
        };
    }

    getEmptyLargeCardData() {
        this.financialsLargeCards.forEach((cardInfo) => {
            cardInfo.largeCardInteriorOptions = {
                action: this.action,
                componentTitle: cardInfo.name,
                searchTerm: '',
                hasData: false,
                isDataReady: true,
                showTitle: true,
                accessToUrl: '',
                uniqueId: this.reusableFunctions.getUniqueId(this.cardDrilldownUrl, ''),
            };
        });
    }

    sanitizeLoanData(data) {
        const loanData = data.filter((item) => item.description.includes('Loan'));
        const otherData = data.filter((item) => !item.description.includes('Loan'));
        otherData.forEach((item, i) => {
            const correspondingLoanData = loanData.find(
                (month) => month.yearMonthCalendar === item.yearMonthCalendar
            );
            correspondingLoanData.period = i;
            item.period = i;
            item.amount -= correspondingLoanData.amount;
            item.bps -= correspondingLoanData.bps;
        });
    }

    applyNewFilters(event: string) {
        this.action = NoDataActionType.filter;
        this.hFilterJSON = event;
        this.initializeLargeCards();
        this.loadSmallCardData();
        this.loadLargeCardData();
    }

    applyDisplayOptions(event: DisplayOptions[]) {
        this.action = NoDataActionType.filter;
        this.displayOptions = event;
        this.setDisplayOptionsSelected();
        this.initializeSmallCards();
        this.loadSmallCardData();
        this.loadLargeCardData();
    }

    setDisplayOptionsSelected(): void {
        this.fromDatePeriod = this.displayOptions.find(
            (item) => item.optionKey === 'dateRangeFinancials'
        ).selectedOptions;
        this.compareTo = this.displayOptions
            .find((item) => item.optionKey === 'compareTo')
            .selectedOptions.replace('Prior', 'Previous');
    }

    getBottomCardData(data) {
        const isFundedCard = data.description === 'Funded';
        const description = isFundedCard ? 'units funded' : 'BPS';
        const value = isFundedCard
            ? this.reusableFunctions.formatNumber(data.nbr)
            : data.bps.toFixed(3);

        return [{ value, description, hasAccess: true }];
    }
}
